import { useEffect, useRef } from 'react';
import { UseFormReturn, useFormState } from 'react-hook-form';
import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import { sortArrayBasedOnConfig } from 'services/schemableFormNavigation/sortItems';
import { DisplayModes } from 'services/webedi/types';
import { setHiddenErrors, useWebEDIStore } from 'stores/webedi/webedi';
import { GenericError } from 'support/helpers/errors/errors';
import { DocumentTypeWithVirtual, PartnerDTO } from 'support/types';
import portalSchema from 'support/types/schema-portal';

import { buildAnnotationItems } from './annotationItems';
import { buildFormErrorItems } from './buildFormErrorItems';
import { formatHiddenError } from './logging';
import { mergeNavigableItems } from './mergeNavigableItems';
import { WebEDIErrorsServiceSingleton } from './serviceSingleton';

type Annotation = portalSchema.components['schemas']['Annotation'];

type UseNavigableItemsArgs = {
  formMethods: UseFormReturn;
  uiConfig: Array<ui.Section> | undefined;
  mode: DisplayModes;
  variables: Record<string, string>;
  documentType: DocumentTypeWithVirtual | undefined;
  senderPartner: Pick<PartnerDTO, 'name' | 'id'> | null | undefined;
  receiverPartner: Pick<PartnerDTO, 'name' | 'id'> | null | undefined;
  annotations: Array<Annotation> | undefined;
  canonical: Record<string, unknown> | undefined | null;
  flattenData?: Record<string, unknown>;
  enabled?: boolean;
};

export const useNavigableAnnotations = ({
  formMethods,
  uiConfig,
  mode,
  variables,
  documentType,
  senderPartner,
  receiverPartner,
  annotations,
  canonical,
  flattenData,
  enabled = true,
}: UseNavigableItemsArgs): void => {
  const { errors, dirtyFields } = useFormState({ control: formMethods.control });
  const isBigMessage = useWebEDIStore((state) => state.isBigMessage);
  const loggedHiddenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const {
      items: formItems,
      hiddenErrors,
      pendingFieldPaths,
    } = buildFormErrorItems({
      formErrors: errors,
      variables,
      uiConfig,
      displayMode: mode,
      dirtyFields,
      isBigMessage,
    });
    const annotationItems = buildAnnotationItems({ annotations, canonical, flattenData, uiConfig });
    const merged = mergeNavigableItems(formItems, annotationItems);

    WebEDIErrorsServiceSingleton.setItems(sortArrayBasedOnConfig(merged, uiConfig ?? []));

    // in a normal message `pendingFieldPaths` are empty, so no special handling needed here
    WebEDIErrorsServiceSingleton.setFieldsPendingValidation(pendingFieldPaths);

    setHiddenErrors(hiddenErrors);

    if (hiddenErrors.length > 0 && documentType && senderPartner && receiverPartner) {
      try {
        hiddenErrors.forEach((error) => {
          if (loggedHiddenIdsRef.current.has(error.id)) {
            return;
          }
          loggedHiddenIdsRef.current.add(error.id);
          const formattedError = formatHiddenError({
            error,
            documentType,
            senderPartner,
            receiverPartner,
            flattenData: formMethods.getValues(),
          });
          logError(new GenericError(`[CS] ${formattedError.message}`, JSON.stringify(formattedError, null, 2)));
        });
      } catch (err) {
        logError(err);
      }
    }
  }, [
    enabled,
    errors,
    dirtyFields,
    uiConfig,
    mode,
    variables,
    documentType,
    senderPartner,
    receiverPartner,
    isBigMessage,
    annotations,
    canonical,
    flattenData,
    formMethods,
  ]);
};
